import { useEffect, useState } from 'react'

import { useT } from '../../i18n'
import type { TranslationKey } from '../../i18n/en'
import { api, type CrewModeFlagMap, type CrewModeStreamId } from '../../utils/api'
import { syncCrewModeFlagsForFarm } from './crewModeFlagSync'

// Server-Side Flag Enforcement v0 follow-up — the admin-surface toggle
// Charter O-4 calls for ("feature-flag toggles live in the administrative
// surface... Crew Mode consumes configuration; it does not edit it").
// Replaces the devtools-console workflow: this writes the real
// FarmFeatureFlag row via /crew-mode-flags, then syncs the local cache
// Sidebar.tsx / CrewModeHome already read, so the change is visible
// immediately without a manual step.

const STREAM_LABEL_KEYS: Record<CrewModeStreamId, TranslationKey> = {
  water: 'crewMode.home.stream.water',
  feed: 'crewMode.home.stream.feed',
  mortality: 'crewMode.home.stream.mortality',
  weightSample: 'crewMode.home.stream.weightSample'
}

const STREAM_ORDER: CrewModeStreamId[] = ['water', 'feed', 'mortality', 'weightSample']

export type CrewModeFarmFlagsPanelProps = {
  farmId: string
  disabled?: boolean
}

export default function CrewModeFarmFlagsPanel({ farmId, disabled = false }: CrewModeFarmFlagsPanelProps) {
  const t = useT()
  const [flags, setFlags] = useState<CrewModeFlagMap | null>(null)
  const [pendingStream, setPendingStream] = useState<CrewModeStreamId | null>(null)
  // Stores the i18n key, not the translated string — translated only at
  // render time, so this state never needs `t` as an effect dependency.
  // `useT()` returns a fresh function identity every render when there's no
  // I18nProvider in the tree (see I18nProvider.tsx's no-context fallback),
  // and putting it in a data-fetching effect's deps caused an infinite
  // render loop.
  const [errorKey, setErrorKey] = useState<TranslationKey | null>(null)

  useEffect(() => {
    let cancelled = false
    setFlags(null)
    setErrorKey(null)
    if (!farmId) return undefined

    api.crewModeFlags
      .list(farmId)
      .then((data) => {
        if (!cancelled) setFlags(data)
      })
      .catch(() => {
        if (!cancelled) setErrorKey('editFarm.crewMode.loadError')
      })

    return () => {
      cancelled = true
    }
  }, [farmId])

  const handleToggle = async (stream: CrewModeStreamId, enabled: boolean) => {
    if (!farmId || disabled) return
    setPendingStream(stream)
    setErrorKey(null)
    try {
      const next = await api.crewModeFlags.set(farmId, stream, enabled)
      setFlags(next)
      // Best-effort — keeps the nav/launcher cache in step immediately
      // instead of waiting for the next opportunistic app-level refresh.
      void syncCrewModeFlagsForFarm(farmId)
    } catch {
      setErrorKey('editFarm.crewMode.saveError')
    } finally {
      setPendingStream(null)
    }
  }

  return (
    <div className="form-card">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 10 }}>
        <strong>📋 {t('editFarm.crewMode.title')}</strong>
        <span style={{ fontSize: 13, color: '#6b7280' }}>{t('editFarm.crewMode.subtitle')}</span>
      </div>

      {!flags && !errorKey && (
        <div style={{ fontSize: 13, color: '#6b7280' }}>{t('editFarm.crewMode.loading')}</div>
      )}

      {errorKey && (
        <div style={{ fontSize: 13, color: '#991b1b', marginBottom: 8 }}>{t(errorKey)}</div>
      )}

      {flags && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {STREAM_ORDER.map((stream) => (
            <label
              key={stream}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                fontSize: 14,
                fontWeight: 600,
                cursor: disabled ? 'default' : 'pointer',
                opacity: pendingStream === stream ? 0.6 : 1
              }}
            >
              <input
                type="checkbox"
                checked={Boolean(flags[stream])}
                disabled={disabled || pendingStream !== null}
                onChange={(event) => void handleToggle(stream, event.target.checked)}
                style={{ width: 18, height: 18 }}
              />
              {t(STREAM_LABEL_KEYS[stream])}
            </label>
          ))}
        </div>
      )}
    </div>
  )
}
